/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// In-chat pinned bar — Figma file Y95LG1oFzU2PHIvFuMYTfJ, node 3466:7567.
// Sits directly under the chat-room header, above the message ScrollView,
// and is only rendered by `ChatRoom` when there is at least one pinned
// message.
//
// Telegram-style SINGLE-ROW bar: a thin segmented vertical indicator on
// the left + a two-line text block (label over preview). The bar shows
// ONE pinned message at a time (the latest by default). Tapping the bar
// cycles to the next pin (wrapping) and calls `onJumpToPinned` for that
// pin so the chat scrolls to it. A small unpin "X" on the right calls
// `onUnpinMessage` for the currently-shown pin.
//
// The left indicator is segmented into N segments where N is the total
// number of pinned messages, capped visually at 5. The segment for the
// currently-shown pin is highlighted brand-indigo; the rest are muted.
//
// Presentational only. The host supplies the pinned-message array and the
// two callbacks via `ChatRoom`'s props; this component owns just the
// `currentIndex` cycling state.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { X } from "lucide-react-native";
import { PreviewLabel } from "../appEvent/PreviewLabel";
import { useColorScheme } from "../foundations/colorScheme";
import { tokens } from "../design-system/tokens";

// accent.indigo highlight for the active indicator segment (Figma).
const ACTIVE_SEGMENT = "#537EFF";
// Muted glyph color for the unpin affordance.
const MUTED_GLYPH = "#888d96";

/**
 * A single pinned-message preview surfaced in the in-chat pinned bar.
 * The host pre-resolves the sender label, preview text, and avatar so the
 * bar stays free of envelope decoding.
 */
export interface PinnedBarMessage {
	readonly id: string;
	readonly senderName?: string | undefined;
	readonly preview: string;
	readonly timestamp: number;
	readonly avatar?:
		| { readonly initials: string; readonly imageUri?: string | undefined }
		| undefined;
}

export interface PinnedBarProps {
	readonly pinnedMessages: ReadonlyArray<PinnedBarMessage>;
	readonly onJumpToPinned?: ((id: string) => void) | undefined;
	readonly onUnpinMessage?: ((id: string) => void) | undefined;
}

// Visual cap — 5+ pins still render exactly five indicator segments.
const MAX_SEGMENTS = 5;

/**
 * Segmented left indicator. Renders `segmentCount` stacked segments that
 * together fill ~26px tall (2px wide), with ~3px gaps. The segment whose
 * index equals `activeSegment` is highlighted brand-indigo; the rest are
 * muted. `segmentCount` is `min(totalPins, 5)`.
 */
function SegmentedIndicator({
	segmentCount,
	activeSegment,
}: {
	readonly segmentCount: number;
	readonly activeSegment: number;
}): JSX.Element {
	return (
		<View className="h-[26px] w-[2px] flex-col justify-between">
			{Array.from({ length: segmentCount }).map((_, i) => (
				<View
					key={i}
					// Each segment grows to share the 26px column equally;
					// the gap is created by the `mt/mb-[1.5px]` margins, which
					// approximate the ~3px inter-segment gaps in the render.
					className={`w-[2px] flex-1 rounded-pill ${
						i !== 0 ? "mt-[1.5px]" : ""
					} ${i !== segmentCount - 1 ? "mb-[1.5px]" : ""}`}
					style={{
						// Active segment is brand-indigo; muted segments are a
						// low-contrast slate. Color is the one bit a utility
						// class can't conditionally express cleanly here.
						backgroundColor:
							i === activeSegment ? ACTIVE_SEGMENT : "#3A3D44",
					}}
				/>
			))}
		</View>
	);
}

export function PinnedBar({
	pinnedMessages,
	onJumpToPinned,
	onUnpinMessage,
}: PinnedBarProps): JSX.Element | null {
	// Most-recent pin first — the bar defaults to previewing `[0]`.
	const ordered = useMemo(
		() => [...pinnedMessages].sort((a, b) => b.timestamp - a.timestamp),
		[pinnedMessages],
	);

	const count = ordered.length;

	// Which pin is currently shown. Clamped whenever the set changes so we
	// never index past the end after an unpin.
	const [currentIndex, setCurrentIndex] = useState(0);
	useEffect(() => {
		setCurrentIndex((i) => (count === 0 ? 0 : Math.min(i, count - 1)));
	}, [count]);

	const current = count > 0 ? ordered[Math.min(currentIndex, count - 1)]! : undefined;

	// Tapping the bar cycles to the next pin (wrapping) and jumps to it.
	// With a single pin there's nothing to cycle — just jump.
	const handlePress = useCallback(() => {
		if (count === 0) return;
		const next = count === 1 ? 0 : (currentIndex + 1) % count;
		setCurrentIndex(next);
		const target = ordered[next];
		if (target !== undefined) onJumpToPinned?.(target.id);
	}, [count, currentIndex, ordered, onJumpToPinned]);

	const handleUnpin = useCallback(() => {
		if (current !== undefined) onUnpinMessage?.(current.id);
	}, [current, onUnpinMessage]);

	const scheme = useColorScheme();

	if (current === undefined) return null;

	// Theme-aware secondary tone for the preview text + its kind icon (lucide
	// needs a resolved colour, not a class).
	const previewColor =
		tokens.color.semantic[scheme === "dark" ? "dark" : "light"].text.secondary;

	// Indicator: one segment per pin, capped at five. The active segment
	// maps the current index onto the (possibly capped) segment range.
	const segmentCount = Math.min(count, MAX_SEGMENTS);
	const activeSegment =
		count <= MAX_SEGMENTS
			? currentIndex
			: Math.round((currentIndex / (count - 1)) * (MAX_SEGMENTS - 1));

	return (
		// Single-row pinned bar — Figma `3466:7567`. Fill `#1A1C21`,
		// 16/4 horizontal/vertical padding, 42px tall.
		<View className="h-[42px] flex-row items-center bg-light-surface-secondary dark:bg-[#1A1C21]">
			<Pressable
				accessibilityRole="button"
				accessibilityLabel={
					count === 1
						? "Jump to pinned message"
						: `Pinned message ${currentIndex + 1} of ${count}. Tap to cycle.`
				}
				onPress={handlePress}
				className="h-full flex-1 flex-row items-center gap-[8px] pl-[16px] active:opacity-70"
			>
				<SegmentedIndicator
					segmentCount={segmentCount}
					activeSegment={activeSegment}
				/>
				<View className="flex-1 flex-col gap-[2px]">
					<Text
						numberOfLines={1}
						className="font-sans text-[12px] font-semibold text-light-text-primary dark:text-[#F5F6F8]"
					>
						{current.senderName ?? "Pinned message"}
					</Text>
					<PreviewLabel
						text={current.preview}
						color={previewColor}
						iconSize={12}
						numberOfLines={1}
						className="font-sans text-[12px]"
					/>
				</View>
			</Pressable>
			{onUnpinMessage !== undefined ? (
				<Pressable
					accessibilityRole="button"
					accessibilityLabel="Unpin message"
					onPress={handleUnpin}
					className="h-full items-center justify-center pl-[8px] pr-[16px] active:opacity-60"
				>
					<X size={16} strokeWidth={2} color={MUTED_GLYPH} />
				</Pressable>
			) : (
				<View className="w-[16px]" />
			)}
		</View>
	);
}
